import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { resolvePeriod, type Period } from "@/lib/period";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface GroupMetrics {
  groupId: string;
  clicks: number;
  conversions: number;
}

// ---------------------------------------------------------------------------
// Fetch raw data
// ---------------------------------------------------------------------------

interface ClickRow {
  group_id: string | null;
  clicked_at: string;
}

interface ConversionRow {
  group_id: string | null;
  status: string;
}

async function fetchGroupMetrics(period: Period): Promise<GroupMetrics[]> {
  const { current } = resolvePeriod(period);
  const curStart = current.start.toISOString();
  const curEnd = current.end.toISOString();

  const [clicksRes, conversionsRes] = await Promise.all([
    supabase
      .from("click_events")
      .select("group_id, clicked_at")
      .gte("clicked_at", curStart)
      .lte("clicked_at", curEnd),

    supabase
      .from("conversion_attempts")
      .select("group_id, status")
      .gte("created_at", curStart)
      .lte("created_at", curEnd),
  ]);

  if (clicksRes.error) throw clicksRes.error;
  if (conversionsRes.error) throw conversionsRes.error;

  // Agregar cliques por group_id no client
  const clicksByGroup = new Map<string, number>();
  for (const row of (clicksRes.data ?? []) as ClickRow[]) {
    if (!row.group_id) continue;
    clicksByGroup.set(row.group_id, (clicksByGroup.get(row.group_id) ?? 0) + 1);
  }

  // Agregar conversoes success por group_id no client (normaliza lowercase)
  const conversionsByGroup = new Map<string, number>();
  for (const row of (conversionsRes.data ?? []) as ConversionRow[]) {
    if (!row.group_id) continue;
    if ((row.status ?? "").toLowerCase().trim() === "success") {
      conversionsByGroup.set(row.group_id, (conversionsByGroup.get(row.group_id) ?? 0) + 1);
    }
  }

  // Unir todos os group_ids encontrados
  const allGroupIds = new Set([...clicksByGroup.keys(), ...conversionsByGroup.keys()]);

  return Array.from(allGroupIds).map((groupId) => ({
    groupId,
    clicks: clicksByGroup.get(groupId) ?? 0,
    conversions: conversionsByGroup.get(groupId) ?? 0,
  }));
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useGroupMetrics(period: Period = "30d") {
  return useQuery<GroupMetrics[], Error>({
    queryKey: ["group-metrics", period],
    queryFn: () => fetchGroupMetrics(period),
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}
